export interface Column {
  id: string;
  [key: string]: any;
}

type ChangeOperation = {
  type: 'update' | 'move' | 'swap';
  indices: number[];
  newValues?: Column[];
};

interface ChangeInfo {
  index: number;
  changeType: 'move' | 'update';
}

interface ArrayStateManagerOptions {
  requestUpdateCallback?: () => void;
  updateDelay?: number;
  reduceMotion?: boolean;
}

const BUFFER_TIME = 100; // ensures animations finish slightly earlier

export class ArrayStateManager {
  public prioritizedIndexes: number[] = [];

  private currentArray: Column[];
  private previousArray: Column[];
  private updateQueue: ChangeOperation[] = [];
  private isUpdating = false;
  private _destroyed = false;
  private requestUpdateCallback?: () => void;
  private updateDelay: number;
  private _reduceMotion: boolean;

  constructor(initialArray: Column[], options: ArrayStateManagerOptions = {}) {
    this.currentArray = [...initialArray];
    this.previousArray = [...initialArray];
    this.requestUpdateCallback = options.requestUpdateCallback;
    this.updateDelay = options.updateDelay ?? 3_000;
    this._reduceMotion = options.reduceMotion ?? false;
  }

  public destroy() {
    this._destroyed = true;
    this.requestUpdateCallback = undefined;
    this.updateQueue = [];
    this.isUpdating = false;
  }

  public set reduceMotion(value: boolean) {
    this._reduceMotion = value;
  }

  public async update(newArray: Column[], updateInterval: number): Promise<void> {
    const changes = this.detectChanges(this.previousArray, newArray);
    const animationsCount = this.countAnimations(changes);
    this.updateDelay = (updateInterval - BUFFER_TIME) / animationsCount; // ensure equal intervals between animations

    if (!this.isUpdating && changes.length > 0) {
      this.updateQueue.push(...changes);
      this.isUpdating = true;
      await this.processQueue();
      this.previousArray = [...newArray];
      this.isUpdating = false;
    }
  }

  public getCurrentState(): Column[] {
    return [...this.currentArray];
  }

  private detectChanges(oldArray: Column[], newArray: Column[]): ChangeOperation[] {
    const swapOperations: ChangeOperation[] = [];
    const updateOperations: ChangeOperation[] = [];
    const moveOperations: ChangeOperation[] = [];

    const oldIndexMap = new Map<string, number>();
    oldArray.forEach((item, index) => {
      oldIndexMap.set(item.id, index);
    });

    const changesInfo: ChangeInfo[] = [];

    for (let newIndex = 0; newIndex < newArray.length; newIndex++) {
      const newItem = newArray[newIndex];
      const oldIndex = oldIndexMap.get(newItem!.id);

      if (oldIndex === undefined) {
        // new item: treat as move
        changesInfo.push({ index: newIndex, changeType: 'move' });
      } else {
        const oldItem = oldArray[oldIndex];
        const isPositionSame = oldIndex === newIndex;
        const isContentSame = this.compareObjects(oldItem!, newItem!);

        // if either the position or content has changed:
        if (!isPositionSame || !isContentSame) {
          // if only the position changed, mark as move; otherwise update.
          changesInfo.push({
            index: newIndex,
            changeType: !isPositionSame ? 'move' : 'update',
          });
        }
      }
    }

    // detect swap pairs from the changes.
    // consider any two changed items as swapped if they have exchanged positions.
    const handledIndices = new Set<number>();
    for (let i = 0; i < changesInfo.length; i++) {
      const { index: indexA } = changesInfo[i]!;
      if (handledIndices.has(indexA)) continue;

      const itemA = newArray[indexA];
      const oldIndexA = oldIndexMap.get(itemA!.id)!;

      for (let j = i + 1; j < changesInfo.length; j++) {
        const { index: indexB } = changesInfo[j]!;
        if (handledIndices.has(indexB)) continue;

        const itemB = newArray[indexB];
        const oldIndexB = oldIndexMap.get(itemB!.id)!;

        if (oldIndexB === indexA && oldIndexA === indexB) {
          // found a swap pair.
          handledIndices.add(indexA);
          handledIndices.add(indexB);
          swapOperations.push({
            type: 'swap',
            indices: [indexA, indexB],
            newValues: [newArray[indexA]!, newArray[indexB]!],
          });
          break;
        }
      }
    }

    for (const info of changesInfo) {
      if (handledIndices.has(info.index)) continue;
      if (info.changeType === 'move') {
        moveOperations.push({
          type: 'move',
          indices: [info.index],
          newValues: [newArray[info.index]!],
        });
      } else {
        updateOperations.push({
          type: 'update',
          indices: [info.index],
          newValues: [newArray[info.index]!],
        });
      }
    }

    return [...moveOperations, ...swapOperations, ...updateOperations];
  }

  private countAnimations(operations: ChangeOperation[]) {
    let moveCount = 0;
    let swapCount = 0;
    let updateCount = 0;

    for (const action of operations) {
      if (action.type === 'move')
        moveCount = 1; // no matter how many, count as 1
      else if (action.type === 'swap') swapCount++;
      else if (action.type === 'update') updateCount++;
    }

    return moveCount + swapCount + updateCount;
  }

  private compareObjects(a: Column, b: Column): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  private async processQueue(): Promise<void> {
    if (this._reduceMotion) {
      for (const op of this.updateQueue) {
        this.applyOperation(op);
      }
      this.updateQueue = [];
      this.requestUpdateCallback?.();
      return;
    }

    // process all move operations at once
    const moveOp = this.updateQueue.filter((op) => op.type === 'move');
    if (moveOp.length) {
      moveOp.forEach((op) => {
        this.applyOperation(op);
      });
      this.updateQueue = this.updateQueue.filter((op) => op.type !== 'move');
      this.requestUpdateCallback?.();
      await this.delay(this.updateDelay);
      if (this._destroyed) return;
    }

    // process remaining update operations with prioritization
    while (this.updateQueue.length > 0) {
      if (this._destroyed) return;

      let op: ChangeOperation | undefined;
      const prioritizedOps = this.updateQueue.filter(
        (item) => item.indices[0] !== undefined && this.prioritizedIndexes.includes(item.indices[0]),
      );
      if (prioritizedOps.length > 0) {
        op = prioritizedOps[Math.floor(Math.random() * prioritizedOps.length)];
        const idx = this.updateQueue.indexOf(op!);
        if (idx !== -1) {
          this.updateQueue.splice(idx, 1);
        }
      } else {
        op = this.updateQueue.pop();
      }

      if (!op) break;

      this.applyOperation(op);
      this.requestUpdateCallback?.();

      if (this.updateQueue.length) {
        await this.delay(this.updateDelay);
        if (this._destroyed) return;
      }
    }
  }

  private applyOperation(operation: ChangeOperation) {
    if (operation.type === 'update' || operation.type === 'move') {
      const idx = operation.indices[0];
      const newObj = operation.newValues![0];
      // @ts-expect-error
      this.currentArray[idx] = { ...newObj };
    } else if (operation.type === 'swap') {
      const [idxA, idxB] = operation.indices;
      const [newObjA, newObjB] = operation.newValues!;
      // @ts-expect-error
      this.currentArray[idxA] = { ...newObjA };
      // @ts-expect-error
      this.currentArray[idxB] = { ...newObjB };
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        clearTimeout(timeout);
        resolve();
      }, ms);
    });
  }
}
